import { isLooseGroup } from "@productlane/lib/src/help-center/loose-group"

import type { ArticleWithSubpages, GroupWithArticles } from "./types"
import { isPublicTreeRow } from "./article-kind"

// UNLISTED is the Agent tier, INTERNAL the AI Assistant's.
export type HiddenTier = "UNLISTED" | "INTERNAL"

export interface HiddenFolder {
	group: GroupWithArticles
	articles: ArticleWithSubpages[]
}

// The section a row belongs to, `null` for the public tree. A link carries no
// visibility the docs site respects, so it counts as agent knowledge unless it
// was marked internal outright.
export const hiddenTierOf = (article: {
	visibility?: string | null
	kind?: "DOC" | "LINK" | null
}): HiddenTier | null => {
	if (isPublicTreeRow(article)) return null
	return article.visibility === "INTERNAL" ? "INTERNAL" : "UNLISTED"
}

// Everything in one group the docs site never renders. A hidden subpage of a
// *public* parent has no hidden parent to nest under, so it surfaces as a row of
// its own rather than disappearing.
function hiddenArticlesInGroup(
	group: GroupWithArticles,
	tier: HiddenTier,
): ArticleWithSubpages[] {
	return group.helpCenterArticles.flatMap((article) => {
		// A hidden parent brings its whole branch along. Dropping the public
		// subpages here left them in neither section, so nothing in the sidebar
		// could open them.
		if (!isPublicTreeRow(article)) {
			return hiddenTierOf(article) === tier
				? [{ ...article, subpages: article.subpages ?? [] }]
				: []
		}
		return (article.subpages ?? [])
			.filter((s) => hiddenTierOf(s) === tier)
			.map((subpage) => ({ ...subpage, subpages: [] }))
	})
}

// The folders a hidden section renders: one per group that holds rows of this
// tier. A group whose hidden rows are all in the other tier drops out, so the
// Agent section never shows an empty Internal folder.
export function collectHiddenFolders(
	articleGroups: readonly GroupWithArticles[],
	tier: HiddenTier,
): HiddenFolder[] {
	return articleGroups.flatMap((group) => {
		const articles = hiddenArticlesInGroup(group, tier)
		return articles.length === 0 ? [] : [{ group, articles }]
	})
}

// Flat rows in render order, so keyboard navigation walks the sidebar the way
// it looks whether or not the section is showing folders.
export function collectHiddenArticles(
	articleGroups: readonly GroupWithArticles[],
	tier: HiddenTier,
): ArticleWithSubpages[] {
	return collectHiddenFolders(articleGroups, tier).flatMap((f) => f.articles)
}

// One group holds rows in both tiers often enough that the Public tree, the
// Agent section and the Internal section each need their own open state for it.
export const hiddenFolderKey = (tier: HiddenTier, groupId: string) =>
	`${tier}:${groupId}`

// The nav open-state key for whichever tree holds this row: its tier folder in
// the Agent or Internal section, or the public group.
export const folderKeyForArticle = (
	article: { visibility?: string | null; kind?: "DOC" | "LINK" | null },
	groupId: string,
) => {
	const tier = hiddenTierOf(article)
	return tier ? hiddenFolderKey(tier, groupId) : groupId
}

// The folders a row may move into. A subpage's folder follows its parent, so
// offering it one of its own would quietly pull it out of the article it belongs
// under, and a single folder leaves nowhere to move to.
export function folderChoicesFor(
	article: ArticleWithSubpages,
	tier: HiddenTier | undefined,
	articleGroups: readonly GroupWithArticles[],
): readonly GroupWithArticles[] {
	// Top-level pages are no folder, so they never show up as a choice.
	const folders = articleGroups.filter((group) => !isLooseGroup(group))
	if (!tier || article.parentArticleId || folders.length < 2) return []
	return folders
}

// Moving a hidden row between folders lands it at the end of the target group.
// Nothing else shifts, so `siblings` stays empty: the gap the row leaves behind
// is harmless, the same way a drag out of a group leaves one.
export function planFolderMove(
	article: ArticleWithSubpages,
	target: GroupWithArticles,
) {
	const lastOrder = target.helpCenterArticles.reduce(
		(max, sibling) => Math.max(max, sibling.order),
		-1,
	)
	return {
		id: article.id,
		helpCenterGroupId: target.id,
		parentArticleId: null,
		order: lastOrder + 1,
		// "" for a row detached from every group, so the move still counts as a
		// regroup and the slug gets rewritten.
		previousGroupId: article.helpCenterGroupId ?? "",
		previousParentArticleId: article.parentArticleId ?? null,
		// Subpages follow their parent, or they render as orphans in the group the
		// parent just left.
		subpageIds: (article.subpages ?? []).map((s) => s.id),
		siblings: [],
	}
}

export const folderHoldsArticle = (folder: HiddenFolder, articleId: string) =>
	folder.articles.some(
		(article) =>
			article.id === articleId ||
			(article.subpages ?? []).some((subpage) => subpage.id === articleId),
	)
